import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import AddUser from './Dialogs/AddUser';
import EditUser from './Dialogs/EditUser';
import DeleteUser from './Dialogs/DeleteUser';
import EmployeeTable from './EmployeeTable';
import SearchFilter from './SearchFilter';
import SalaryDialog from './Dialogs/salary/SalaryDialog';
import CalculateMonthlyHoursDialog from './Dialogs/eMonthlyHours';
import TotalAndAvgHoursDialog from './Dialogs/TotalAndAvgHoursDialog';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const ViewEmployees = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Manage rows per page
  const [search, setSearch] = useState('');
  const [openCalculateMonthlyDialog, setOpenCalculateMonthlyDialog] = useState(false);
  const [openTotalAndAvgHoursDialog, setOpenTotalAndAvgHoursDialog] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [openSalaryDialog, setOpenSalaryDialog] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openEditUser, setOpenEditUser] = useState(false);
  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchEmployees = useCallback(async () => {
    try {
      const employeesCollection = collection(db, 'employees');
      const employeeSnapshot = await getDocs(employeesCollection);
      const employeeList = employeeSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEmployees(employeeList);
    } catch (error) {
      console.error("Error fetching employees: ", error);
    }
  }, []);

  const refreshEmployees = useCallback(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    refreshEmployees();
  }, [refreshEmployees]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handlePositionChange = (event) => {
    setSelectedPosition(event.target.value);
    setPage(0); // Reset to first page on filter change
  };

  const handleOpenAddUser = () => setOpenAddUser(true);
  const handleCloseAddUser = () => {
    setOpenAddUser(false);
    refreshEmployees();
  };

  const handleOpenEditUser = (user) => {
    setSelectedUser(user);
    setOpenEditUser(true);
  };
  const handleCloseEditUser = () => {
    setOpenEditUser(false);
    refreshEmployees();
  };

  const handleOpenCalculateMonthlyDialog = (user) => {
    setSelectedUser(user);
    setOpenCalculateMonthlyDialog(true);
  };

  const handleCloseCalculateMonthlyDialog = () => {
    setOpenCalculateMonthlyDialog(false);
  };

  const handleOpenDeleteUser = (user) => {
    setSelectedUser(user);
    setOpenDeleteUser(true);
  };
  const handleCloseDeleteUser = () => {
    setOpenDeleteUser(false);
    refreshEmployees();
  };

  const handleOpenSalaryDialog = (user) => {
    setSelectedUser(user);
    setOpenSalaryDialog(true);
  };
  const handleCloseSalaryDialog = () => {
    setOpenSalaryDialog(false);
  };

  const handleSaveUser = (updatedUser) => {
    console.log('Updated User:', updatedUser);
    handleCloseEditUser();
  };

  const handleOpenTotalAndAvgHoursDialog = (user) => {
    setSelectedUser(user);
    setOpenTotalAndAvgHoursDialog(true);  // Open the new dialog
  };

  const handleCloseTotalAndAvgHoursDialog = () => {
    setOpenTotalAndAvgHoursDialog(false);  // Close the new dialog
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const filteredData = employees.filter(user => {
    const matchesSearch = user.id.toLowerCase().includes(search.toLowerCase()) ||
                          user.name.toLowerCase().includes(search.toLowerCase()) ||
                          user.position.toLowerCase().includes(search.toLowerCase()) ||
                          user.email.toLowerCase().includes(search.toLowerCase());

    const matchesPosition = selectedPosition ? user.position === selectedPosition : true;

    return matchesSearch && matchesPosition;
  });

  const displayedData = filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const positions = Array.from(new Set(employees.map(user => user.position)));

  return (
    <Box sx={{ flexGrow: 1, padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#39B54A' }}>
          View Employees
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 3,
          marginTop: 2,
        }}
      >
        <SearchFilter
          search={search}
          selectedPosition={selectedPosition}
          onSearchChange={handleSearchChange}
          onPositionChange={handlePositionChange}
          onAddClick={handleOpenAddUser}
          positions={positions}
        />
        <EmployeeTable
          data={displayedData}
          onSalary={handleOpenSalaryDialog}
          onEdit={handleOpenEditUser}
          onDelete={handleOpenDeleteUser}
          onMonthly={handleOpenCalculateMonthlyDialog} 
          onViewTotalAvgHours={handleOpenTotalAndAvgHoursDialog}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalEntries={filteredData.length}
        />
      </Box>

      <AddUser open={openAddUser} onClose={handleCloseAddUser} />
      {selectedUser && (
        <EditUser
          open={openEditUser}
          onClose={handleCloseEditUser}
          user={selectedUser}
          onSave={handleSaveUser}
        />
      )}
      {selectedUser && (
        <DeleteUser
          open={openDeleteUser}
          onClose={handleCloseDeleteUser}
          userId={selectedUser.id}
          onDelete={() => setSelectedUser(null)}
          onSuccess={() => {
            setSnackbarMessage('Employee deleted successfully');
            setSnackbarOpen(true);
          }}
        />
      )}
      {selectedUser && (
        <SalaryDialog
          open={openSalaryDialog}
          onClose={handleCloseSalaryDialog}
          user={selectedUser}
          onSave={(salaryDetails) => {
            console.log('Salary Details:', salaryDetails);
            handleCloseSalaryDialog();
          }}
        />
      )}
      {selectedUser && (
        <CalculateMonthlyHoursDialog
          open={openCalculateMonthlyDialog}
          onClose={handleCloseCalculateMonthlyDialog}
          user={selectedUser}
        />
      )}
      {selectedUser && (
        <TotalAndAvgHoursDialog
          open={openTotalAndAvgHoursDialog} 
          onClose={handleCloseTotalAndAvgHoursDialog} 
          user={selectedUser} />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewEmployees;
